import { Box, Flex, Grid, Heading, Spinner, Text } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FiEdit, FiPlus, FiTrash2, FiUsers } from "react-icons/fi"

import { OrganisationsService } from "@/client"
import type { OrganisationRead } from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/organisations")({
  component: Organizations,
})

function Organizations() {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const navigate = useNavigate()
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const [orgToDelete, setOrgToDelete] = useState<OrganisationRead | null>(null)

  const {
    data: organizations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organizations"],
    queryFn: () =>
      OrganisationsService.readOrganisations({ skip: 0, limit: 100 }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      OrganisationsService.deleteOrganisation({ organisationId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      showToast.showSuccessToast(t("admin:orgs.deleted"))
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleDelete = (org: OrganisationRead) => {
    setOrgToDelete(org)
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (error) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("admin:orgs.errorLoading")}
        </Heading>
        <Text color={sc.textMuted}>{t("admin:orgs.errorLoadingDesc")}</Text>
      </VisionCard>
    )
  }

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 3, md: 0 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box minW={0}>
          <Heading size={{ base: "lg", md: "xl" }} color={sc.heading} mb={2}>
            {t("admin:orgs.title")}
          </Heading>
          <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
            {t("admin:orgs.subtitle")}
          </Text>
        </Box>
        <VisionButton
          variant="brand"
          onClick={() =>
            navigate({
              to: "/organisation-form",
              search: { organisationId: undefined },
            })
          }
        >
          <FiPlus />
          {t("admin:orgs.add")}
        </VisionButton>
      </Flex>

      <Grid
        templateColumns={{
          base: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
        }}
        gap={{ base: 3, md: 4, lg: 6 }}
      >
        {organizations.map((org) => (
          <VisionCard key={org.id} variant="gradient">
            <Flex direction="column" h="100%">
              <Flex justify="space-between" align="flex-start" mb={4}>
                <Box flex="1">
                  <Heading size="md" color={sc.heading} mb={2}>
                    {org.name}
                  </Heading>
                  <Text color={sc.textSecondary} fontSize="sm" lineClamp={2}>
                    {org.description || t("admin:orgs.noDescription")}
                  </Text>
                </Box>
                <IconBox variant="glass" size="md">
                  <FiUsers fontSize="20px" color={sc.iconColor} />
                </IconBox>
              </Flex>

              <Flex gap={2} mt="auto">
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: "/organisation-form",
                      search: { organisationId: org.id },
                    })
                  }
                  flex="1"
                >
                  <FiEdit /> {t("common:actions.edit")}
                </VisionButton>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  colorPalette="red"
                  onClick={() => handleDelete(org)}
                >
                  <FiTrash2 /> {t("common:actions.delete")}
                </VisionButton>
              </Flex>
            </Flex>
          </VisionCard>
        ))}

        {organizations.length === 0 && (
          <VisionCard variant="outline">
            <Flex direction="column" align="center" justify="center" py={8}>
              <IconBox variant="glass" size="lg" mb={4}>
                <FiUsers fontSize="24px" color={sc.iconColor} />
              </IconBox>
              <Heading size="md" color={sc.heading} mb={2}>
                {t("admin:orgs.noOrgs")}
              </Heading>
              <Text color={sc.textMuted} textAlign="center" mb={4}>
                {t("admin:orgs.noOrgsDesc")}
              </Text>
              <VisionButton
                variant="brand"
                onClick={() =>
                  navigate({
                    to: "/organisation-form",
                    search: { organisationId: undefined },
                  })
                }
              >
                <FiPlus /> {t("admin:orgs.add")}
              </VisionButton>
            </Flex>
          </VisionCard>
        )}
      </Grid>

      <ConfirmDialog
        open={!!orgToDelete}
        onClose={() => setOrgToDelete(null)}
        onConfirm={() => {
          if (orgToDelete) deleteMutation.mutate(orgToDelete.id)
        }}
        title={t("common:confirm.deleteTitle", { type: t("common:org.label") })}
        message={t("admin:orgs.confirmDelete", { name: orgToDelete?.name })}
        loading={deleteMutation.isPending}
      />
    </Box>
  )
}
